import React, { PropsWithChildren } from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useTransitionToNextTierMutation } from 'queries/shifts'
import { useShiftDetailWorkflowsContextValue } from 'pages/Shifts/ShiftDetail/context/ShiftDetailWorkflowsProvider'

type SwitchTiersFlowProviderContextState = {
  actions: {
    transitionToNextTier: ReturnType<
      typeof useTransitionToNextTierMutation
    >['mutate']
    close: () => void
  }
  state: {
    mutationState: {
      isPending: boolean
      isError: boolean
      errorMessage: string | undefined
    }
  }
}

const [Context, useSwitchTiersFlowContextValue] =
  createContext<SwitchTiersFlowProviderContextState>({
    name: 'SwitchTiersFlowContext',
  })

const SwitchTiersFlowContextProvider = ({
  children,
  listingId,
  shiftId,
  close,
}: PropsWithChildren<{
  listingId: number
  shiftId: number
  close: () => void
}>) => {
  const { mutate, isPending, isError, error } = useTransitionToNextTierMutation(
    listingId,
    shiftId
  )

  const value: SwitchTiersFlowProviderContextState = {
    actions: {
      transitionToNextTier: mutate,
      close,
    },
    state: {
      mutationState: {
        isPending,
        isError,
        errorMessage: parseErrorMessage(error),
      },
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const SwitchTiersFlowProvider = ({ children }: PropsWithChildren) => {
  const {
    switchTiersFlow: { isOpen, data: switchTiersFlowData, close },
  } = useShiftDetailWorkflowsContextValue()

  if (isOpen && !switchTiersFlowData) {
    throw new Error(
      'Unable to find switch tiers flow data. Please report this error to the engineering team.'
    )
  }

  if (!switchTiersFlowData) {
    return null
  }

  return (
    <SwitchTiersFlowContextProvider
      listingId={switchTiersFlowData.listingId}
      shiftId={switchTiersFlowData.shiftId}
      close={close}
    >
      {children}
    </SwitchTiersFlowContextProvider>
  )
}

export { useSwitchTiersFlowContextValue }
